import { jsx as _jsx } from "react/jsx-runtime";
import { useBreakpointsContext } from "@air/provider-media-query";
import { useShortIdContext } from "@air/provider-short-id";
import { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AssetsSearchBar } from "~/components/AssetsSearchBar/AssetsSearchBar";
import { PublicFiltersBar } from "~/components/Filters/v2/components/PublicFiltersBar";
import { useBoardSearchToken } from "~/components/Search/SearchTokens/useBoardSearchToken";
import { MobileSearchCommand } from "~/components/SearchCommand/MobileSearchCommand";
import { SearchCommand } from "~/components/SearchCommand/SearchCommand";
import { createShortUrlBoardRoute } from "~/constants/routes";
import { QueryParamNames } from "~/constants/search";
import { useAppliedFiltersCount } from "~/hooks/filters/useAppliedFiltersCount";
import { useIsFlattenedView } from "~/hooks/search/useIsFlattenedView";
import { useSearchParams } from "~/hooks/search/useSearchParams";
import { useStickyResult } from "~/hooks/useStickyResult";
import { useTemporaryFeatureFlags } from "~/hooks/useTemporaryFeatureFlags";
import { centralizedBoardAncestorsSelector, centralizedBoardTitleSelector } from "~/store/centralizedBoard/selectors";
import { useGoToPublicBoard } from "~/swr-hooks/boards/useGoToPublicBoard";
import { usePublicSearchSuggestions } from "~/swr-hooks/search/usePublicSearchSuggestions";
import { pushWithExistingQuery } from "~/utils/PathUtils";
export const PublicSearch = (param)=>{
    let { onCloseClick } = param;
    const { searchImprovements } = useTemporaryFeatureFlags();
    const { filtersCount } = useAppliedFiltersCount();
    const [searchTerm, setSearchTerm] = useState("");
    const { isFlattenedView } = useIsFlattenedView();
    const { isAboveMediumScreen } = useBreakpointsContext();
    const { shortId } = useShortIdContext();
    const boardName = useSelector(centralizedBoardTitleSelector);
    const boardAncestors = useSelector(centralizedBoardAncestorsSelector);
    const searchBarRef = useRef(null);
    const [isFocused, setIsFocused] = useState(false);
    const { search, setSearch, clearSearch } = useSearchParams();
    const onClose = useCallback(()=>{
        clearSearch();
        onCloseClick();
    }, [
        clearSearch,
        onCloseClick
    ]);
    const { goToPublicBoard } = useGoToPublicBoard();
    const onBoardClick = useCallback((board)=>goToPublicBoard(board, "search"), [
        goToPublicBoard
    ]);
    const { data, refetch } = usePublicSearchSuggestions({
        search: searchTerm,
        shortId,
        canFetch: isFocused
    });
    // this hook prevents passing empty data to SearchBar while suggestions are loading
    // It is used to keep previous suggestions visible while new ones are not returned yet
    const suggestions = useStickyResult(data);
    const onFocusChange = useCallback((isFocused)=>{
        setIsFocused(isFocused);
        if (isFocused) {
            refetch();
        }
    }, [
        refetch
    ]);
    const { onBackspacePress, wasTokenRemoved, BoardToken, showToken } = useBoardSearchToken({
        isFocused,
        hasValue: !!searchTerm,
        boardName,
        searchBarRef
    });
    const showBoardToken = isFlattenedView || boardAncestors.length > 0;
    const onApplyTextSearch = useCallback((newTerm)=>{
        if (wasTokenRemoved) {
            const newQuery = {
                [QueryParamNames.Search]: newTerm
            };
            if (boardAncestors.length > 0) {
                pushWithExistingQuery({
                    path: shortId ? createShortUrlBoardRoute(shortId, boardAncestors[0].id) : "/",
                    newQuery
                });
            } else {
                pushWithExistingQuery({
                    newQuery
                });
            }
        } else {
            setSearch(newTerm);
        }
        setSearchTerm(newTerm);
    }, [
        boardAncestors,
        wasTokenRemoved,
        setSearch,
        shortId
    ]);
    if (searchImprovements) {
        if (isAboveMediumScreen) {
            return /*#__PURE__*/ _jsx(SearchCommand, {
                filtersCount: isAboveMediumScreen ? filtersCount : undefined,
                filterBar: isAboveMediumScreen && /*#__PURE__*/ _jsx(PublicFiltersBar, {
                    trackLocation: "search"
                }),
                onBoardClick: onBoardClick,
                onClear: isAboveMediumScreen ? undefined : onClose,
                onOpenChange: onFocusChange,
                onSearchChange: setSearchTerm,
                onSuggestionClick: onApplyTextSearch,
                placeholder: "Search board",
                suggestions: suggestions
            });
        }
        return /*#__PURE__*/ _jsx(MobileSearchCommand, {
            filtersCount: isAboveMediumScreen ? filtersCount : undefined,
            onBoardClick: onBoardClick,
            onClear: isAboveMediumScreen ? undefined : onClose,
            onOpenChange: onFocusChange,
            onSearchChange: setSearchTerm,
            onSuggestionClick: onApplyTextSearch,
            placeholder: "Search board",
            suggestions: suggestions
        });
    }
    return /*#__PURE__*/ _jsx(AssetsSearchBar, {
        ref: searchBarRef,
        onBackspacePress: onBackspacePress,
        trackLocation: "Public Board",
        searchTerm: search,
        onCloseClick: onClose,
        onApplyTextSearch: onApplyTextSearch,
        placeholder: "Search board",
        onFocusChange: onFocusChange,
        onSearchClear: showToken,
        onSearchTermChange: setSearchTerm,
        textSuggestions: suggestions === null || suggestions === void 0 ? void 0 : suggestions.suggestions,
        boardSuggestions: suggestions === null || suggestions === void 0 ? void 0 : suggestions.boards,
        onBoardClick: onBoardClick,
        children: showBoardToken && /*#__PURE__*/ _jsx("div", {
            className: "flex",
            children: BoardToken
        })
    });
};
